import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const roleFeatureQueryKeys = {
  all: ['roleFeature'],
  list: () => [...roleFeatureQueryKeys.all, 'list'],
  detail: (roleName) => [...roleFeatureQueryKeys.all, 'detail', roleName],
};

/**
 * 모든 역할-피처 매핑 조회
 * @returns {Array} Items 배열 반환 (각 아이템은 RoleName과 FeatureNames 포함)
 */
export const useRoleFeatureClaims = () => {
  return useQuery({
    queryKey: roleFeatureQueryKeys.list(),
    queryFn: async () => {
      const response = await api.get('/drivecore/id/api/RoleFeatureClaim');
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
    cacheTime: 10 * 60 * 1000, // 10분간 캐시 보관
  });
};

/**
 * 특정 역할들의 피처 권한 조회
 * @param {string[]} roleNames - 역할 이름 배열
 * @returns {string[]} 모든 역할의 피처 합집합
 */
export const getRoleFeaturesFromClaims = (roleFeatureClaims, roleNames) => {
  if (!roleFeatureClaims?.Items || !Array.isArray(roleNames)) {
    return [];
  }

  // 모든 역할의 피처를 합집합으로 합치기
  const allFeatures = new Set();

  roleNames.forEach((roleName) => {
    const roleFeature = roleFeatureClaims.Items.find(
      (item) => item.RoleName === roleName
    );

    if (roleFeature?.FeatureNames && Array.isArray(roleFeature.FeatureNames)) {
      roleFeature.FeatureNames.forEach((feature) => {
        allFeatures.add(feature);
      });
    }
  });

  return Array.from(allFeatures);
};

/**
 * 역할-피처 매핑 저장
 * POST /drivecore/id/api/RoleFeatureClaim/featureclaims
 */
export const useUpdateRoleFeatureClaims = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleName, featureNames }) => {
      const response = await api.post('/drivecore/id/api/RoleFeatureClaim/featureclaims', {
        roleName,
        featureNames,
      });
      return response;
    },
    onSuccess: () => {
      // 역할-피처 매핑 캐시 무효화
      queryClient.invalidateQueries(roleFeatureQueryKeys.list());
    },
  });
};

